import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PostCard from '@/components/base/PostCard';
import { searchPosts, getAllTags } from '@/utils/search';
import { Post } from '@/mocks/posts';
import { useSeoMeta } from '@/hooks/useSeoMeta';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const searchPageSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'SearchResultsPage',
  name: 'Search Articles — roameurope.blog',
  description: 'Search hundreds of relationship articles by keyword, topic, or tag on roameurope.blog.',
  url: `${BASE_URL}/search`,
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialTag = searchParams.get('tag') || '';

  const [query, setQuery] = useState(initialQuery);
  const [activeTag, setActiveTag] = useState(initialTag);
  const [results, setResults] = useState<Post[]>([]);
  const allTags = getAllTags();

  useSeoMeta({
    title: query ? `Search: "${query}" — roameurope.blog` : 'Search Articles — roameurope.blog',
    description: 'Search hundreds of relationship articles by keyword, topic, or tag on roameurope.blog.',
    url: '/search',
    type: 'website',
  });

  const runSearch = useCallback((q: string, tag: string) => {
    const found = searchPosts(q, tag || undefined);
    setResults(found);
    const params: Record<string, string> = {};
    if (q) params.q = q;
    if (tag) params.tag = tag;
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    runSearch(initialQuery, initialTag);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    runSearch(val, activeTag);
  };

  const handleTagClick = (tag: string) => {
    const next = activeTag === tag ? '' : tag;
    setActiveTag(next);
    runSearch(query, next);
  };

  const clearAll = () => {
    setQuery('');
    setActiveTag('');
    runSearch('', '');
  };

  const hasFilters = query.trim() || activeTag;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: searchPageSchema }}
      />
      <Navbar />

      {/* Header */}
      <section className="pt-28 pb-10 bg-white border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2"
            style={{ fontFamily: "'Teko', sans-serif", letterSpacing: '0.02em' }}>
            Search Articles
          </h1>
          <p className="text-stone-500 text-sm mb-6">
            Find relationship advice by keyword or topic tag
          </p>

          {/* Search input */}
          <div className="relative">
            <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
              <i className="ri-search-line text-lg" />
            </div>
            <input
              type="text"
              value={query}
              onChange={handleQueryChange}
              placeholder="Search by keyword, topic, or author..."
              className="w-full pl-11 pr-12 py-3.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); runSearch('', activeTag); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Tags */}
      <section className="bg-white border-b border-stone-100 py-4">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider mr-1 whitespace-nowrap">
              Filter by tag:
            </span>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagClick(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTag === tag
                    ? 'bg-rose-500 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-rose-50 hover:text-rose-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 md:px-8 py-10">
        {/* Result count + clear */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-stone-500">
            {hasFilters ? (
              <>
                <span className="font-semibold text-stone-800">{results.length}</span>
                {' '}article{results.length !== 1 ? 's' : ''} found
                {query && <> for &ldquo;<span className="text-rose-600 font-medium">{query}</span>&rdquo;</>}
                {activeTag && <> tagged <span className="text-rose-600 font-medium">&ldquo;{activeTag}&rdquo;</span></>}
              </>
            ) : (
              <>Showing all <span className="font-semibold text-stone-800">{results.length}</span> published articles</>
            )}
          </p>
          {hasFilters && (
            <button
              onClick={clearAll}
              className="text-xs text-rose-500 hover:text-rose-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-close-circle-line" /> Clear filters
            </button>
          )}
        </div>

        {/* No results */}
        {results.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-stone-300">
              <i className="ri-search-2-line text-5xl" />
            </div>
            <h2 className="text-xl font-semibold text-stone-700 mb-2">No articles found</h2>
            <p className="text-stone-400 text-sm mb-6">
              Try a different keyword or remove the tag filter
            </p>
            <button
              onClick={clearAll}
              className="px-5 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Show all articles
            </button>
          </div>
        )}

        {/* Results grid */}
        {results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {results.map((post) => (
              <SearchResultCard key={post.id} post={post} query={query} activeTag={activeTag} />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

interface SearchResultCardProps {
  post: Post;
  query: string;
  activeTag: string;
}

function SearchResultCard({ post, query, activeTag }: SearchResultCardProps) {
  const highlight = (text: string, q: string) => {
    if (!q.trim()) return text;
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="bg-rose-100 text-rose-700 rounded px-0.5">{part}</mark>
        : part
    );
  };

  return (
    <Link
      to={`/post/${post.slug}`}
      className="group bg-white rounded-xl border border-stone-100 overflow-hidden hover:border-rose-200 transition-all cursor-pointer block"
    >
      <div className="w-full h-44 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-medium text-rose-500 uppercase tracking-wide whitespace-nowrap">
            {post.category}
          </span>
          <span className="text-stone-300 text-xs">·</span>
          <span className="text-xs text-stone-400 whitespace-nowrap">{post.readTime} min read</span>
        </div>
        <h3 className="text-sm font-semibold text-stone-800 leading-snug mb-2 group-hover:text-rose-600 transition-colors line-clamp-2">
          {highlight(post.title, query)}
        </h3>
        <p className="text-xs text-stone-500 line-clamp-2 mb-3">
          {highlight(post.excerpt, query)}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
                activeTag === tag
                  ? 'bg-rose-500 text-white'
                  : 'bg-stone-100 text-stone-500'
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
